import React, { useState, useRef, useEffect, useCallback } from "react";
import { 
  X, Minus, Maximize2, Minimize2, Send, Loader2, 
  HardHat, MessageSquare, Sparkles, CheckCircle2,
  AlertTriangle, Plus, Trash2, Paperclip, FileImage, FileText, Image,
  Mic, MicOff, Square
} from "lucide-react";

/**
 * AiCopilotModal - "ESTIMATING COMMAND"
 * 
 * A draggable, persistent AI chat window that acts as a 30-year veteran partner.
 * Features:
 * - Draggable floating window
 * - Persistent chat history (survives close/reopen)
 * - Real-time estimate analysis and suggestions
 * - Natural language interaction
 * 
 * FIX: Flex column layout for proper scrolling and input anchoring
 */

const AiCopilotModal = ({
  isOpen,
  onClose,
  onMinimize,
  isMinimized,
  chatHistory,
  setChatHistory,
  onSendMessage,
  onFileUpload, // NEW: Handler for file uploads
  isLoading,
  estimateContext,
  clientName,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: window.innerHeight - 520 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null); // NEW: Attached file state
  const [filePreview, setFilePreview] = useState(null); // NEW: File preview URL
  
  // Voice-to-text state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  
  // Check if speech recognition is supported
  const voiceSupported = typeof window !== 'undefined' && 
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  
  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';
        
        recognitionRef.current.onresult = (event) => {
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            }
          }
          
          if (finalTranscript) {
            setInputValue(prev => prev + finalTranscript);
          }
        };
        
        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
        
        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);
  
  // Toggle voice recording
  const toggleVoiceRecording = useCallback(() => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputValue(''); // Clear input when starting fresh recording
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);
  
  // Determine if estimate is empty for context-aware placeholder
  const estimateIsEmpty = !estimateContext?.groups?.length || 
    estimateContext.groups.every(g => !g.items?.length);
  
  // Personalized client name
  const displayClientName = clientName || "your client";
  
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null); // NEW: File input ref

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      });
    }
  }, [chatHistory, isLoading]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);
  
  // Cleanup file preview URL on unmount
  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload an image (PNG, JPG, WEBP) or PDF file.');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      
      setAttachedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RAPID FIRE: Clipboard Paste Handler
  // Allows Ctrl+V to paste images directly into chat
  // ═══════════════════════════════════════════════════════════════════════════
  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check for image files in clipboard
    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior
        
        const file = item.getAsFile();
        if (file) {
          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            alert('Pasted image must be less than 10MB.');
            return;
          }
          
          // Generate a filename for pasted images
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const pastedFile = new File([file], `screenshot-${timestamp}.png`, {
            type: file.type,
          });
          
          setAttachedFile(pastedFile);
          setFilePreview(URL.createObjectURL(pastedFile));
          
          console.log('[RAPID FIRE] Image pasted from clipboard:', pastedFile.name);
        }
        return; // Only handle first image
      }
    }
  }, []);

  // Attach paste listener to input
  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('paste', handlePaste);
      return () => {
        inputElement.removeEventListener('paste', handlePaste);
      };
    }
  }, [handlePaste]);
  
  // Remove attached file
  const removeAttachedFile = () => {
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }
    setAttachedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle dragging
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.drag-handle') && !isMaximized) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  }, [position, isMaximized]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle send message (with optional file)
  const handleSend = () => {
    if ((inputValue.trim() || attachedFile) && !isLoading) {
      // If file is attached, call file upload handler
      if (attachedFile) {
        if (onFileUpload) {
          onFileUpload(attachedFile, inputValue.trim());
        }
        removeAttachedFile();
      } else {
        onSendMessage(inputValue.trim());
      }
      setInputValue("");
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Toggle maximize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  if (!isOpen) return null;

  // Minimized state - small floating button
  if (isMinimized) {
    return (
      <button
        onClick={onMinimize}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 shadow-2xl shadow-amber-500/30 flex items-center justify-center hover:scale-110 transition-transform group"
        style={{ zIndex: 2147483647 }} // MAX z-index to ensure always on top
        title="Open ESTIMATING COMMAND"
      >
        <HardHat className="h-7 w-7 text-white" />
        {chatHistory.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full text-xs font-bold text-white flex items-center justify-center">
            {chatHistory.filter(m => m.role === 'assistant' && !m.read).length || ''}
          </span>
        )}
      </button>
    );
  }

  // Mobile detection
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  
  const modalStyle = isMaximized || isMobile
    ? { top: isMobile ? 0 : 20, left: isMobile ? 0 : 20, right: isMobile ? 0 : 20, bottom: isMobile ? 0 : 20, width: 'auto', height: 'auto' }
    : { top: position.y, left: position.x, width: 400, height: 500 };

  return (
    <div
      ref={modalRef}
      className={`fixed flex flex-col rounded-none sm:rounded-2xl shadow-2xl shadow-black/50 ${
        isDragging ? 'cursor-grabbing select-none' : ''
      }`}
      style={{
        ...modalStyle,
        backgroundColor: '#0f172a', // Solid dark background (slate-900)
        border: isMobile ? 'none' : '1px solid rgba(51, 65, 85, 0.8)', // slate-700/80
        overflow: 'visible', // FIX: Ensure widget is not artificially clipped
        zIndex: 2147483647, // FIX: MAX z-index to force to front of all layers
        position: 'fixed', // Ensure position is set for z-index to take effect
        isolation: 'isolate', // Create new stacking context
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header - Drag Handle - SOLID BACKGROUND to prevent bleed-through */}
      <div 
        className="drag-handle flex items-center justify-between px-4 py-3 border-b border-slate-800 cursor-grab active:cursor-grabbing flex-shrink-0"
        style={{
          backgroundColor: '#1e293b', // Solid slate-800 background
          position: 'relative',
          zIndex: 10,
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <HardHat className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">ESTIMATING COMMAND</h3>
            <p className="text-xs text-amber-400/80">30-Year Veteran Partner</p>
          </div>
        </div>
        
        {/* Window Controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={onMinimize}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            title="Minimize"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            onClick={toggleMaximize}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            title={isMaximized ? "Restore" : "Maximize"}
          >
            {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Chat History - Solid background with proper scrolling */}
      <div
        ref={chatContainerRef}
        className="flex-1 p-4 space-y-4"
        style={{ 
          backgroundColor: '#0f172a', // Solid slate-900 background
          overflowY: 'auto', // FIX: Enable vertical scrolling
          overflowX: 'hidden',
          minHeight: 0, // FIX: Allow flex item to shrink below content size
        }}
      >
        {chatHistory.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30 flex items-center justify-center mb-4">
              <HardHat className="h-8 w-8 text-amber-400" />
            </div>
            <h4 className="text-white font-semibold mb-2">ESTIMATING COMMAND Online</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              What are we building for <span className="text-amber-400 font-semibold">{displayClientName}</span> today?
            </p>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Just tell me the scope and I&apos;ll build a complete estimate with materials, labor, and everything you might be missing.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <span className="px-2 py-1 text-xs bg-slate-800 text-slate-400 rounded-lg border border-slate-700 cursor-pointer hover:border-amber-500/50" onClick={() => setInputValue("New 20x20 deck with composite decking")}>20x20 deck</span>
              <span className="px-2 py-1 text-xs bg-slate-800 text-slate-400 rounded-lg border border-slate-700 cursor-pointer hover:border-amber-500/50" onClick={() => setInputValue("10x10 bathroom remodel with new vanity and tile")}>Bathroom remodel</span>
              <span className="px-2 py-1 text-xs bg-slate-800 text-slate-400 rounded-lg border border-slate-700 cursor-pointer hover:border-amber-500/50" onClick={() => setInputValue("Kitchen renovation with new cabinets and countertops")}>Kitchen renovation</span>
            </div>
          </div>
        ) : (
          chatHistory.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <HardHat className="h-4 w-4 text-amber-400" />
            </div>
            <div className="bg-slate-800/50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Analyzing scope...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area - Solid background, anchored to bottom */}
      <div 
        className="p-3 border-t border-slate-800 flex-shrink-0"
        style={{ 
          backgroundColor: '#1e293b', // Solid slate-800 background
          position: 'relative',
          zIndex: 5, // FIX: Ensure input area is above chat content
        }}
      >
        {/* File Preview */}
        {attachedFile && (
          <div className="mb-2 p-2 bg-slate-800/60 border border-slate-700/50 rounded-lg flex items-center gap-3">
            {filePreview ? (
              <img src={filePreview} alt="Preview" className="w-12 h-12 rounded object-cover" />
            ) : (
              <div className="w-12 h-12 rounded bg-slate-700 flex items-center justify-center">
                <FileText className="h-6 w-6 text-amber-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{attachedFile.name}</p>
              <p className="text-xs text-slate-400">
                {attachedFile.type.includes('pdf') ? 'PDF Document' : 'Image'} • {(attachedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              onClick={removeAttachedFile}
              className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        )}
        
        <div className="flex items-end gap-2">
          {/* File Upload Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-amber-400 hover:border-amber-500/50 transition-all disabled:opacity-50"
            title="Upload floor plan, blueprint, or image"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={attachedFile 
                ? "Add instructions for this file (optional)..." 
                : isListening
                  ? "🎤 Listening... speak now"
                  : estimateIsEmpty && chatHistory.length === 0 
                    ? "Tell me what we're building... (e.g., '10x10 bathroom remodel')" 
                    : "Reply to your Superintendent..."}
              rows={1}
              className={`w-full bg-slate-800/60 border rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 resize-none transition-all ${
                isListening ? 'border-red-500/70 ring-2 ring-red-500/30' : 'border-slate-700/50'
              }`}
              style={{ minHeight: '44px', maxHeight: '120px' }}
              disabled={isLoading}
            />
            {isListening && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-xs text-red-400 font-medium">REC</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Voice Recording Button */}
          {voiceSupported && (
            <button
              onClick={toggleVoiceRecording}
              disabled={isLoading}
              className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                isListening
                  ? 'bg-red-500/20 border-2 border-red-500 text-red-400 animate-pulse'
                  : 'bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50'
              } disabled:opacity-50`}
              title={isListening ? "Stop recording" : "Voice input - speak your project description"}
            >
              {isListening ? (
                <Square className="h-4 w-4 fill-current" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
          )}
          
          <button
            onClick={handleSend}
            disabled={isLoading || (!inputValue.trim() && !attachedFile)}
            className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
              isLoading || (!inputValue.trim() && !attachedFile)
                ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/25'
            }`}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Quick Actions - FIX: Lower z-index to not obscure messages */}
        <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1" style={{ position: 'relative', zIndex: 1 }}>
          <button
            onClick={() => setInputValue("Yes, add those items")}
            className="px-2.5 py-1 text-xs bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors whitespace-nowrap"
          >
            ✓ Add suggested
          </button>
          <button
            onClick={() => setInputValue("Skip those for now")}
            className="px-2.5 py-1 text-xs bg-slate-700/50 text-slate-400 rounded-lg hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            Skip for now
          </button>
          <button
            onClick={() => setInputValue("What else might I be missing?")}
            className="px-2.5 py-1 text-xs bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors whitespace-nowrap"
          >
            What else?
          </button>
        </div>
      </div>
    </div>
  );
};

// Chat Message Component
// Handles: clarification_questions, review_flags (assumptions), and suggested_items (add-ons)
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const isAction = message.type === 'action';
  const isConstructionInstructions = message.type === 'construction_instructions';
  
  if (isAction) {
    return (
      <div className="flex items-center justify-center">
        <div className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
          <div className="flex items-center gap-2 text-xs text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>{message.content}</span>
          </div>
        </div>
      </div>
    );
  }

  // Special handling for construction instructions
  if (isConstructionInstructions) {
    return (
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30">
          <HardHat className="h-4 w-4 text-amber-400" />
        </div>
        
        {/* Message Bubble */}
        <div className="max-w-[85%] bg-slate-800/50 border border-slate-700/50 rounded-2xl rounded-tl-sm px-4 py-3">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700/50">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-300">Construction Instructions Generated</span>
          </div>
          
          {/* Message */}
          <p className="text-sm text-white leading-relaxed">{message.content}</p>
          
          {/* Download Buttons */}
          {message.download_urls && (
            <div className="mt-3 flex gap-2">
              {message.download_urls.pdf && (
                <a 
                  href={message.download_urls.pdf}
                  download
                  className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-lg text-xs font-medium text-emerald-300 transition-colors flex items-center gap-1"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Download PDF
                </a>
              )}
              {message.download_urls.word && (
                <a 
                  href={message.download_urls.word}
                  download
                  className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 rounded-lg text-xs font-medium text-blue-300 transition-colors flex items-center gap-1"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Download Word
                </a>
              )}
            </div>
          )}
          
          {/* Document Preview */}
          {message.document && (
            <div className="mt-3 p-3 bg-slate-900/50 border border-slate-700/30 rounded-lg max-h-48 overflow-y-auto">
              <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono leading-relaxed">
                {message.document.split('\n').slice(0, 20).join('\n')}
                {message.document.split('\n').length > 20 && '\n...'}
              </pre>
            </div>
          )}
          
          {/* Timestamp */}
          <p className="text-xs mt-2 text-slate-500">
            {message.timestamp || 'Just now'}
          </p>
        </div>
      </div>
    );
  }

  // Extract review flags (assumptions/notes) from message
  const reviewFlags = message.review_flags || [];
  
  // Extract suggested items (actionable add-ons) - filter out assumption-like entries
  const filterActualSuggestions = (items) => {
    if (!items || !Array.isArray(items)) return [];
    
    // Keywords that indicate assumptions/notes, NOT actionable items
    const assumptionKeywords = [
      'assumed', 'assumption', 'not included', 'excluded', 'permits', 
      'engineering', 'inspection', 'based on', 'standard', 'typical',
      'unless', 'if needed', 'may vary', 'verify', 'confirm'
    ];
    
    return items.filter(item => {
      const text = (typeof item === 'string' ? item : item.item || item.name || '').toLowerCase();
      // Filter out items that look like assumptions
      return !assumptionKeywords.some(keyword => text.includes(keyword));
    });
  };
  
  // Get actual suggestions (actionable items only)
  const suggestions = filterActualSuggestions(message.suggestions || message.suggested_items || []);
  
  // Get clarification questions if present
  const clarificationQuestions = message.clarification_questions || [];

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isUser 
          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30'
          : 'bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30'
      }`}>
        {isUser ? (
          <MessageSquare className="h-4 w-4 text-blue-400" />
        ) : (
          <HardHat className="h-4 w-4 text-amber-400" />
        )}
      </div>
      
      {/* Message Bubble */}
      <div className={`max-w-[85%] ${
        isUser 
          ? 'bg-blue-600/20 border border-blue-500/30 rounded-2xl rounded-tr-sm'
          : 'bg-slate-800/50 border border-slate-700/50 rounded-2xl rounded-tl-sm'
      } px-4 py-3`}>
        <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
          {(() => {
            // FIX: Parse and clean up raw JSON content to display human-readable text
            let content = message.content || '';
            
            // If content looks like raw JSON, try to extract meaningful text
            if (typeof content === 'string') {
              // Remove markdown JSON code blocks
              content = content.replace(/```json[\s\S]*?```/g, '').replace(/```[\s\S]*?```/g, '');
              
              // If content starts with { or [, it's likely raw JSON
              const trimmed = content.trim();
              if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 10) {
                try {
                  const parsed = JSON.parse(trimmed);
                  
                  // Try to extract meaningful fields from parsed JSON
                  if (parsed.assistant_message) {
                    content = parsed.assistant_message;
                  } else if (parsed.message) {
                    content = parsed.message;
                  } else if (parsed.response) {
                    content = parsed.response;
                  } else if (parsed.explanation) {
                    content = parsed.explanation;
                  } else if (parsed.change_summary) {
                    content = parsed.change_summary;
                  } else if (Array.isArray(parsed) && parsed.length > 0) {
                    // If it's an array of questions or items
                    if (typeof parsed[0] === 'string') {
                      content = parsed.join('\n• ');
                    }
                  } else {
                    // Fallback: show a generic message instead of raw JSON
                    content = "I've processed your request. Please check the estimate above for the updates.";
                  }
                } catch {
                  // Not valid JSON, keep original content but clean it up
                  content = trimmed;
                }
              }
            }
            
            return content.trim() || "I've updated the estimate. Let me know if you need any changes.";
          })()}
        </div>
        
        {/* Clarification Questions from Scope Gate */}
        {clarificationQuestions && clarificationQuestions.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-cyan-400/80 font-semibold uppercase tracking-wider flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Questions to Answer:
            </p>
            {clarificationQuestions.map((question, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-2 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg"
              >
                <div className="w-5 h-5 rounded bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-cyan-400">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-cyan-200">{question}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 mt-2 italic">
              Please answer these questions so I can build an accurate estimate.
            </p>
          </div>
        )}
        
        {/* Review Flags / Assumptions & Notes - Display in muted style */}
        {reviewFlags && reviewFlags.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs text-slate-400/80 font-semibold uppercase tracking-wider">
              Assumptions & Notes:
            </p>
            <div className="space-y-1">
              {reviewFlags.map((flag, idx) => {
                const flagText = typeof flag === 'string' 
                  ? flag 
                  : flag.message || flag.text || flag.description || JSON.stringify(flag);
                return (
                  <div key={idx} className="flex items-start gap-2 text-xs text-slate-400">
                    <span className="text-slate-500">•</span>
                    <span>{flagText}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Optional Add-Ons - Only show actual actionable items */}
        {suggestions && suggestions.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-emerald-400/80 font-semibold uppercase tracking-wider">
              Optional Add-Ons:
            </p>
            {suggestions.map((suggestion, idx) => {
              const itemName = typeof suggestion === 'string' 
                ? suggestion 
                : suggestion.item || suggestion.itemName || suggestion.name;
              const cost = suggestion.cost || suggestion.unitCost;
              const reason = suggestion.reason;
              
              return (
                <div 
                  key={idx}
                  className="flex items-start gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg"
                >
                  <div className="w-5 h-5 rounded bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Plus className="h-3 w-3 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-emerald-200">{itemName}</p>
                    {reason && (
                      <p className="text-xs text-slate-400 mt-0.5">{reason}</p>
                    )}
                    {cost && (
                      <p className="text-xs text-emerald-400 mt-1">Est. ${cost}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Timestamp */}
        <p className={`text-xs mt-2 ${isUser ? 'text-blue-400/60' : 'text-slate-500'}`}>
          {message.timestamp || 'Just now'}
        </p>
      </div>
    </div>
  );
};

export default AiCopilotModal;
